/**
 * @fileoverview Business-logic layer for the banking feature.
 * @description Ported from the InsightBox Banking design system's own `data/derived.ts`. Every
 * selector takes the fetched `BankDataPayload` as an explicit first parameter rather than reading
 * it from a module-level singleton (the reference's own `getBankData()`) — this app's convention
 * is a `useBankingData()` TanStack Query hook per the Banking module plan, and explicit parameters
 * keep these functions pure and independently testable. `branchId` throughout this file is one of
 * three things: `ALL_BRANCHES_ID`, a real branch id, or a region pseudo-id (`isRegionId`) — `core()`
 * is the one place that distinction is resolved, via `regionCoreYear`, so every other selector below
 * (built on top of `core()`) supports all three automatically.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import {
  ALL_BRANCHES_ID,
  BRANCHES,
  FISCAL_YEARS,
  FY_MONTHS,
  isRegionId,
  locationForRegionId,
  prevFY,
} from '@/features/banking/constants/banking-options.constants'
import { branchCoreYear, regionCoreYear } from '@/features/banking/lib/branch-financials'
import {
  largestRemainderRound,
  largestRemainderRoundDecimal,
} from '@/features/banking/lib/rounding'

import type { FiscalYear } from '@/features/banking/constants/banking-options.constants'
import type {
  BankDataPayload,
  CoreYear,
  NpaSectorEntry,
} from '@/features/banking/types/banking.types'

const FIRST_FY = FISCAL_YEARS[0]
const LAST_FY = FISCAL_YEARS[FISCAL_YEARS.length - 1]!

/**
 * @description The scope's display name — a real branch's `name`, "All <Region> Branches" for a
 * region pseudo-id, or "All Branches" for the bank-wide aggregate.
 * @param {string} branchId - `ALL_BRANCHES_ID`, a real branch id, or a region pseudo-id.
 * @returns {string} The scope's display name.
 */
export function branchLabel(branchId: string): string {
  if (branchId === ALL_BRANCHES_ID) return 'All Branches'
  if (isRegionId(branchId)) {
    const location = locationForRegionId(branchId)
    return location ? `All ${location} Branches` : 'All Branches'
  }
  return BRANCHES.find((branch) => branch.id === branchId)?.name ?? 'All Branches'
}

/**
 * @description The regulatory minimum CRAR %, sourced from the fetched dataset rather than a
 * hardcoded constant.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @returns {number} `data.meta.crarMinimum`.
 */
export function crarMinimum(data: BankDataPayload): number {
  return data.meta.crarMinimum
}

/**
 * @description The single consolidated source every selector below reads through — "All
 * Branches" returns the bank-wide `core.all[fy]` untouched; a region pseudo-id returns that
 * region's aggregate via `regionCoreYear`; any real branch returns its own figures via
 * `branchCoreYear` (CRAR excluded from both — bank-level only at every scope).
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default), a region pseudo-id, or a real branch id.
 * @returns {CoreYear} That scope's figures for `fy`.
 */
export function core(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
): CoreYear {
  if (branchId === ALL_BRANCHES_ID) return data.core.all[fy]
  if (isRegionId(branchId)) {
    const location = locationForRegionId(branchId)
    if (location) return regionCoreYear(data, fy, location)
  }
  return branchCoreYear(data, fy, branchId)
}

/**
 * @description Deposits plus advances.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 * @returns {number} `deposits + advances`, in ₹ Cr.
 */
export function totalBusiness(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
): number {
  const c = core(data, fy, branchId)
  return c.deposits + c.advances
}

/**
 * @description The Credit-Deposit ratio.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 * @returns {number} `(advances / deposits) * 100`.
 */
export function cdRatio(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
): number {
  const c = core(data, fy, branchId)
  return (c.advances / c.deposits) * 100
}

/**
 * @description Year-over-year growth for any metric, given a selector closing over the caller's
 * chosen data/branch scope.
 * @param {FiscalYear} fy - The fiscal year to compare against its predecessor.
 * @param {(fy: FiscalYear) => number} selector - Reads the metric for a given fiscal year.
 * @returns {number | null} The YoY growth %, or `null` if `fy` is the earliest year or the prior
 * year's value was zero.
 */
export function yoyGrowth(fy: FiscalYear, selector: (fy: FiscalYear) => number): number | null {
  const prev = prevFY(fy)
  if (!prev) return null
  const curVal = selector(fy)
  const prevVal = selector(prev)
  if (prevVal === 0) return null
  return ((curVal - prevVal) / prevVal) * 100
}

/**
 * @description Growth from the earliest to the latest fiscal year in `FISCAL_YEARS`.
 * @param {(fy: FiscalYear) => number} selector - Reads the metric for a given fiscal year.
 * @returns {number} The 5-year growth %.
 */
export function fiveYearGrowth(selector: (fy: FiscalYear) => number): number {
  const first = selector(FIRST_FY)
  const last = selector(LAST_FY)
  return ((last - first) / first) * 100
}

/**
 * @description The fiscal year with the highest value of a metric.
 * @param {(fy: FiscalYear) => number} selector - Reads the metric for a given fiscal year.
 * @returns {{ fy: FiscalYear; value: number }} The peak year and its value.
 */
export function peakYear(selector: (fy: FiscalYear) => number): { fy: FiscalYear; value: number } {
  let best: FiscalYear = FIRST_FY
  for (const fy of FISCAL_YEARS) {
    if (selector(fy) > selector(best)) best = fy
  }
  return { fy: best, value: selector(best) }
}

/**
 * @description The fiscal year with the lowest value of a metric.
 * @param {(fy: FiscalYear) => number} selector - Reads the metric for a given fiscal year.
 * @returns {{ fy: FiscalYear; value: number }} The trough year and its value.
 */
export function troughYear(selector: (fy: FiscalYear) => number): {
  fy: FiscalYear
  value: number
} {
  let worst: FiscalYear = FIRST_FY
  for (const fy of FISCAL_YEARS) {
    if (selector(fy) < selector(worst)) worst = fy
  }
  return { fy: worst, value: selector(worst) }
}

/**
 * @description Whether `fy` is the highest-value year across the full 5-year window.
 * @param {FiscalYear} fy - The fiscal year to check.
 * @param {(fy: FiscalYear) => number} selector - Reads the metric for a given fiscal year.
 * @returns {boolean} True if `fy` is the peak year.
 */
export function isHighestIn5Y(fy: FiscalYear, selector: (fy: FiscalYear) => number): boolean {
  return peakYear(selector).fy === fy
}

/**
 * @description Sum of an additive metric (₹ amounts, counts) across the full 5-year window — the
 * "5 Years" KPI figure for anything meaningfully cumulative. Never use this for a ratio/percentage
 * (CRAR%, NPA%, an average, ...) — summing those produces a meaningless number; such metrics
 * should show the latest year's value instead.
 * @param {(fy: FiscalYear) => number} selector - Reads the metric for a given fiscal year.
 * @returns {number} The sum across every fiscal year.
 */
export function sumAcrossYears(selector: (fy: FiscalYear) => number): number {
  return FISCAL_YEARS.reduce((sum, fy) => sum + selector(fy), 0)
}

type OverviewRowKey = keyof CoreYear | 'totalBusiness' | 'cdRatio'
type OverviewUnit = 'cr' | 'pct' | 'int'

export interface OverviewMetricGroup {
  groupLabel: string
  unit: OverviewUnit
  rows: { key: OverviewRowKey; label: string }[]
}

// Overall Bank Position table, grouped by unit so figures of different scales (headcount vs
// percentage vs ₹ Cr) never sit in one undifferentiated list.
export const OVERVIEW_METRIC_GROUPS: OverviewMetricGroup[] = [
  {
    groupLabel: 'Share Holders',
    unit: 'int',
    rows: [{ key: 'totalMembers', label: 'Share Holders' }],
  },
  { groupLabel: 'CD Ratio (%)', unit: 'pct', rows: [{ key: 'cdRatio', label: 'CD Ratio' }] },
  {
    groupLabel: 'Key Financial Metrics (₹ In Crores)',
    unit: 'cr',
    rows: [
      { key: 'shareCapital', label: 'Share Capital' },
      { key: 'reserves', label: 'Reserves' },
      { key: 'deposits', label: 'Total Deposits' },
      { key: 'advances', label: 'Total Advances' },
      { key: 'investments', label: 'Investments' },
      { key: 'netProfit', label: 'Net Profit' },
      { key: 'totalBusiness', label: 'Total Business' },
    ],
  },
]

/**
 * @description Reads a single Bank Overview row's value, dispatching the two derived keys
 * (`totalBusiness`/`cdRatio`) to their own selectors and everything else straight to `core`.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} key - One of `OVERVIEW_METRIC_GROUPS`' row keys.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 * @returns {number} The row's value.
 */
export function overviewMetricValue(
  data: BankDataPayload,
  fy: FiscalYear,
  key: string,
  branchId: string = ALL_BRANCHES_ID,
): number {
  if (key === 'totalBusiness') return totalBusiness(data, fy, branchId)
  if (key === 'cdRatio') return cdRatio(data, fy, branchId)
  return (core(data, fy, branchId) as unknown as Record<string, number>)[key]!
}

// ---------------------------------------------------------------------------
// Membership & Share Capital
// ---------------------------------------------------------------------------

export interface MemberSeriesPoint {
  fy: FiscalYear
  total: number
  added: number
  cancelled: number
  net: number
}

/**
 * @description The 5-year member movement series for a scope.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 * @returns {MemberSeriesPoint[]} One point per fiscal year.
 */
export function memberSeries(
  data: BankDataPayload,
  branchId: string = ALL_BRANCHES_ID,
): MemberSeriesPoint[] {
  return FISCAL_YEARS.map((fy) => {
    const c = core(data, fy, branchId)
    return {
      fy,
      total: c.totalMembers,
      added: c.newMembers,
      cancelled: c.cancelledMembers,
      net: c.newMembers - c.cancelledMembers,
    }
  })
}

/**
 * @description Share-capital segmentation bands, scaled from the FY25/All-Branches snapshot
 * ratios proportionally to the selected scope's totals, so the bands always sum to exactly that
 * scope's headline member/capital figures.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 * @returns {(BankDataPayload['constants']['shareCapitalBands'][number] & { members: number; capitalCr: number })[]}
 * The bands, member/capital figures rescaled and reconciled to the scope's totals.
 */
export function shareCapitalBands(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
) {
  const c = core(data, fy, branchId)
  const base = data.core.all.FY25
  const memberScale = c.totalMembers / base.totalMembers
  const capitalScale = c.shareCapital / base.shareCapital
  const bands = data.constants.shareCapitalBands
  const memberCounts = largestRemainderRound(
    bands.map((band) => band.members * memberScale),
    c.totalMembers,
  )
  const capitalAmounts = largestRemainderRoundDecimal(
    bands.map((band) => band.capitalCr * capitalScale),
    c.shareCapital,
  )
  return bands.map((band, index) => ({
    ...band,
    members: memberCounts[index]!,
    capitalCr: capitalAmounts[index]!,
  }))
}

// ---------------------------------------------------------------------------
// Customer Base
// ---------------------------------------------------------------------------

/**
 * @description All-branch ranking by customer count (or scoped to `branchIds`).
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string[]} [branchIds] - Restricts the ranking to these branch ids. Defaults to every
 * branch.
 * @returns {{ branch: (typeof BRANCHES)[number]; count: number; sharePct: number }[]} Branches
 * sorted by customer count, descending.
 */
export function branchCustomers(data: BankDataPayload, fy: FiscalYear, branchIds?: string[]) {
  const scope = branchIds ? BRANCHES.filter((branch) => branchIds.includes(branch.id)) : BRANCHES
  const rows = scope.map((branch) => ({ branch, count: core(data, fy, branch.id).totalCustomers }))
  const total = rows.reduce((sum, row) => sum + row.count, 0)
  return rows
    .map((row) => ({ ...row, sharePct: (row.count / total) * 100 }))
    .sort((a, b) => b.count - a.count)
}

/**
 * @description The combined customer share of the top `n` branches.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {number} n - How many top branches to include.
 * @param {string[]} [branchIds] - Restricts the ranking to these branch ids first.
 * @returns {number} The top-`n` share, as a percentage.
 */
export function topNShare(
  data: BankDataPayload,
  fy: FiscalYear,
  n: number,
  branchIds?: string[],
): number {
  const rows = branchCustomers(data, fy, branchIds)
  const total = rows.reduce((sum, row) => sum + row.count, 0)
  const topSum = rows.slice(0, n).reduce((sum, row) => sum + row.count, 0)
  return (topSum / total) * 100
}

/**
 * @description Each branch's FY21→FY25 customer growth, sorted fastest-growing first. Total
 * Customers is a stock (a headcount at each year-end), so summing it across years the way
 * `sumAcrossYears` does for a flow would double-count the same customers repeatedly — growth is
 * the meaningful "5 Years" view for a stock instead.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {string[]} [branchIds] - Restricts the ranking to these branch ids. Defaults to every
 * branch.
 * @returns {{ branch: (typeof BRANCHES)[number]; startCount: number; endCount: number; netGain: number; growthPct: number }[]}
 */
export function branchCustomerGrowth5Y(data: BankDataPayload, branchIds?: string[]) {
  const scope = branchIds ? BRANCHES.filter((branch) => branchIds.includes(branch.id)) : BRANCHES
  return scope
    .map((branch) => {
      const startCount = core(data, 'FY21', branch.id).totalCustomers
      const endCount = core(data, 'FY25', branch.id).totalCustomers
      const netGain = endCount - startCount
      return { branch, startCount, endCount, netGain, growthPct: (netGain / startCount) * 100 }
    })
    .sort((a, b) => b.growthPct - a.growthPct)
}

interface MonthlyPoint {
  fy: string
  value: number
  growthPct: number | null
}

/**
 * @description Within-year value for a single metric, Apr → Mar, interpolated between last
 * year's and this year's published year-end figures via the shared seasonal acquisition shape —
 * March is forced to exactly match the FY-end value so it always ties out to the annual figure
 * shown elsewhere. Backs every "drill into one year" monthly chart — a simplified smooth glide
 * toward the reported year-end number, not real intra-year accounting.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to interpolate within.
 * @param {(fy: FiscalYear) => number} selector - Reads the metric for a given fiscal year.
 * @param {boolean} round - Whether to round each month's value to the nearest integer.
 * @returns {MonthlyPoint[]} One point per month (`fy` holds the month name here, matching the
 * chart's x-axis key convention).
 */
function monthlyInterpolate(
  data: BankDataPayload,
  fy: FiscalYear,
  selector: (fy: FiscalYear) => number,
  round: boolean,
): MonthlyPoint[] {
  const shape = data.constants.monthlyAcquisitionShape
  const endValue = selector(fy)
  const prev = prevFY(fy)
  const startValue = prev ? selector(prev) : endValue - (selector('FY22') - selector('FY21'))
  const delta = endValue - startValue

  let cumulative = 0
  const values = FY_MONTHS.map((month, i) => {
    cumulative += shape[i]!
    const isLast = i === FY_MONTHS.length - 1
    const value = isLast ? endValue : startValue + delta * cumulative
    return { month, value: round ? Math.round(value) : value }
  })

  return values.map((v, i) => {
    const prevValue = values[i - 1]?.value
    return {
      fy: v.month,
      value: v.value,
      growthPct:
        i === 0 || prevValue === undefined ? null : ((v.value - prevValue) / prevValue) * 100,
    }
  })
}

/**
 * @description Monthly customer-count drill-down for a fiscal year.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to interpolate within.
 * @param {string} branchId - `ALL_BRANCHES_ID` or a real branch id.
 * @returns {MonthlyPoint[]} One point per month.
 */
export function monthlyCustomers(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string,
): MonthlyPoint[] {
  return monthlyInterpolate(data, fy, (f) => core(data, f, branchId).totalCustomers, true)
}

/**
 * @description Generic monthly drill-down for any single-value trend metric (Total Business, Net
 * Profit, CRAR, ...) — pass a selector closing over the caller's chosen branch scope (or ignoring
 * it, for bank-level-only metrics like CRAR).
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to interpolate within.
 * @param {(fy: FiscalYear) => number} selector - Reads the metric for a given fiscal year.
 * @returns {MonthlyPoint[]} One point per month.
 */
export function monthlySeries(
  data: BankDataPayload,
  fy: FiscalYear,
  selector: (fy: FiscalYear) => number,
): MonthlyPoint[] {
  return monthlyInterpolate(data, fy, selector, false)
}

// A handful of branches read as "wavy" every year — a dip (or bump) partway through before
// swinging the other way to close at the real FY-end figure — rather than every branch gliding
// smoothly in a single direction. Same 12-month total, just a different in-between path: still
// sums to the shape convention (values total 1) so March always lands on the exact year-end
// member count. This is a chart-display choice, not backend data, so it stays hardcoded here
// rather than moving into the fetched dataset.
const MEMBER_WAVY_SHAPE = [
  -0.05, -0.09, -0.11, -0.05, 0.05, 0.1, 0.15, 0.18, 0.2, 0.22, 0.2, 0.2,
] as const
const MEMBER_WAVY_BRANCH_IDS = new Set([
  'hadapsar',
  'baner',
  'wakad',
  'camp',
  'yerawada',
  'sinhagad-road',
])

interface MonthlyMemberMovementPoint {
  fy: string
  total: number
  added: number
  cancelled: number
}

/**
 * @description Monthly Member Count drill-down. Self-contained (doesn't reuse
 * `monthlyInterpolate`) because it needs full control over two guarantees the generic helper
 * doesn't provide: (1) `added`/`cancelled` must sum across all 12 months to EXACTLY that year's
 * `newMembers`/`cancelledMembers` — the same figures the annual KPI cards show, not just
 * approximately; (2) for a "wavy" branch (a mid-year dip before recovering to the FY-end figure),
 * the dip's depth is capped so it never demands more monthly cancellations than that year's actual
 * `cancelledMembers` can supply — found by blending `MEMBER_WAVY_SHAPE` toward the smooth shape
 * just far enough to stay inside that budget, rather than inventing extra cancellations beyond the
 * real annual figure to explain a deeper dip.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to interpolate within.
 * @param {string} branchId - `ALL_BRANCHES_ID` or a real branch id.
 * @returns {MonthlyMemberMovementPoint[]} One point per month.
 */
export function monthlyMemberMovement(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string,
): MonthlyMemberMovementPoint[] {
  const acquisitionShape = data.constants.monthlyAcquisitionShape
  const isWavy = MEMBER_WAVY_BRANCH_IDS.has(branchId)

  const endValue = core(data, fy, branchId).totalMembers
  const yearAdded = core(data, fy, branchId).newMembers
  const yearCancelled = core(data, fy, branchId).cancelledMembers

  const prev = prevFY(fy)
  const startTotal = prev
    ? core(data, prev, branchId).totalMembers
    : endValue - (yearAdded - yearCancelled)
  const delta = endValue - startTotal

  /**
   * @description Blends the smooth acquisition shape toward the "wavy" shape by factor `t`.
   * @param {number} t - 0 = fully smooth, 1 = fully wavy.
   * @returns {number[]} The blended monthly shape.
   */
  function shapeAt(t: number): number[] {
    return isWavy
      ? acquisitionShape.map((v, i) => t * MEMBER_WAVY_SHAPE[i]! + (1 - t) * v)
      : [...acquisitionShape]
  }

  /**
   * @description Rounded month-end running totals for a given monthly shape.
   * @param {number[]} shape - The monthly acquisition shape to apply.
   * @returns {number[]} One running total per month.
   */
  function monthlyTotals(shape: number[]): number[] {
    let cumulative = 0
    return FY_MONTHS.map((_, i) => {
      cumulative += shape[i]!
      const isLast = i === FY_MONTHS.length - 1
      return Math.round(isLast ? endValue : startTotal + delta * cumulative)
    })
  }

  /**
   * @description The minimum total cancellations a given blend factor's month-over-month dips
   * would require.
   * @param {number} t - 0 = fully smooth, 1 = fully wavy.
   * @returns {number} The required cancellation budget.
   */
  function minRequiredChurn(t: number): number {
    let prevTotal = startTotal
    let required = 0
    for (const total of monthlyTotals(shapeAt(t))) {
      if (total - prevTotal < 0) required += prevTotal - total
      prevTotal = total
    }
    return required
  }

  // t=0 (fully smooth) is always feasible — a monotonic shape only ever needs at most |delta| of
  // cancellations, and |delta| <= yearCancelled by construction whenever delta is negative. So
  // binary-search for the largest t in [0, 1] that stays within budget.
  let t = 1
  if (isWavy && minRequiredChurn(1) > yearCancelled) {
    let lo = 0
    let hi = 1
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2
      if (minRequiredChurn(mid) > yearCancelled) hi = mid
      else lo = mid
    }
    t = lo
  }

  const totals = monthlyTotals(shapeAt(t))
  let prevTotal = startTotal
  const nets = totals.map((total) => {
    const net = total - prevTotal
    prevTotal = total
    return net
  })

  // Every month gets at least the cancellations it needs to keep added >= 0 that month; whatever
  // annual churn is left over is spread proportionally by the smooth acquisition shape
  // (largest-remainder rounded) — so cancelled sums to exactly yearCancelled and added sums to
  // exactly yearAdded, matching the annual KPI cards precisely.
  const minRequired = nets.map((net) => Math.max(0, -net))
  const slack = yearCancelled - minRequired.reduce((sum, value) => sum + value, 0)
  const weightSum = acquisitionShape.reduce((sum, value) => sum + value, 0)
  const extra = largestRemainderRound(
    acquisitionShape.map((weight) => (slack * weight) / weightSum),
    slack,
  )

  const cancelled = minRequired.map((value, i) => value + extra[i]!)
  const added = cancelled.map((c, i) => c + nets[i]!)

  return FY_MONTHS.map((month, i) => ({
    fy: month,
    total: totals[i]!,
    added: added[i]!,
    cancelled: cancelled[i]!,
  }))
}

// ---------------------------------------------------------------------------
// Deposits Portfolio
// ---------------------------------------------------------------------------

/**
 * @description Deposit-type mix (term/savings/current), amounts and interest reconciled to the
 * scope's actual deposit/interest totals.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 */
export function depositMix(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
) {
  const deposits = core(data, fy, branchId).deposits
  const totalInterest = deposits * data.constants.totalInterestPaidRateOnDeposits
  const mix = data.constants.depositMix
  const amounts = largestRemainderRoundDecimal(
    mix.map((d) => deposits * d.depositShare),
    deposits,
  )
  const interests = largestRemainderRoundDecimal(
    mix.map((d) => totalInterest * d.interestShare),
    totalInterest,
  )
  return mix.map((d, i) => ({
    key: d.key,
    label: d.label,
    amountCr: amounts[i]!,
    depositSharePct: d.depositShare * 100,
    interestCr: interests[i]!,
    interestSharePct: d.interestShare * 100,
  }))
}

/**
 * @description Total interest paid on the deposit book.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 * @returns {number} Interest paid, in ₹ Cr.
 */
export function totalInterestPaid(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
): number {
  return core(data, fy, branchId).deposits * data.constants.totalInterestPaidRateOnDeposits
}

/**
 * @description Average deposit per account, by type and overall, plus accounts-per-employee.
 * Account counts scale by the branch's actual share of that year's customers (not its
 * deposit/income share) — a branch carrying a much bigger deposit share than customer share
 * (treasury/bulk deposits) would otherwise cancel out and make the average deposit per account
 * identical at every branch.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 */
export function depositAverages(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
) {
  const c = core(data, fy, branchId)
  const bankCustomers =
    branchId === ALL_BRANCHES_ID ? c.totalCustomers : core(data, fy, ALL_BRANCHES_ID).totalCustomers
  const accountScale = branchId === ALL_BRANCHES_ID ? 1 : c.totalCustomers / bankCustomers
  const depositAccountsForYear = data.constants.depositAccountsByYear[fy]
  const accounts = {
    term: Math.round(depositAccountsForYear.term * accountScale),
    savings: Math.round(depositAccountsForYear.savings * accountScale),
    current: Math.round(depositAccountsForYear.current * accountScale),
  }
  const deposits = c.deposits
  const mix = depositMix(data, fy, branchId)
  const termCr = mix.find((m) => m.key === 'term')!.amountCr
  const savingsCr = mix.find((m) => m.key === 'savings')!.amountCr
  const currentCr = mix.find((m) => m.key === 'current')!.amountCr
  const totalAccounts = accounts.term + accounts.savings + accounts.current
  const employees = c.employees
  return {
    avgTermPerAccount: (termCr * 1e7) / Math.max(accounts.term, 1),
    avgSavingsPerAccount: (savingsCr * 1e7) / Math.max(accounts.savings, 1),
    avgCurrentPerAccount: (currentCr * 1e7) / Math.max(accounts.current, 1),
    avgPerAccount: (deposits * 1e7) / Math.max(totalAccounts, 1),
    totalAccounts,
    accounts,
    employees: Math.max(employees, 1),
    accountsPerEmployee: totalAccounts / Math.max(employees, 1),
    totalDepositsCr: deposits,
  }
}

// ---------------------------------------------------------------------------
// Loans & Advances
// ---------------------------------------------------------------------------

/**
 * @description Loan-product mix, amounts reconciled to the scope's actual advances total, sorted
 * largest first.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 */
export function loanProductMix(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
) {
  const advances = core(data, fy, branchId).advances
  const mix = data.constants.loanProductMix
  const amounts = largestRemainderRoundDecimal(
    mix.map((p) => advances * p.share),
    advances,
  )
  return mix
    .map((p, i) => ({ key: p.key, label: p.label, amountCr: amounts[i]!, sharePct: p.share * 100 }))
    .sort((a, b) => b.amountCr - a.amountCr)
}

/**
 * @description Loan interest-rate-slab mix, amounts reconciled to the scope's actual advances
 * total.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 */
export function loanRateSlabs(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
) {
  const advances = core(data, fy, branchId).advances
  const slabs = data.constants.loanRateSlabs
  const amounts = largestRemainderRoundDecimal(
    slabs.map((s) => advances * s.share),
    advances,
  )
  return slabs.map((s, i) => ({
    key: s.key,
    label: s.label,
    amountCr: amounts[i]!,
    sharePct: s.share * 100,
  }))
}

// ---------------------------------------------------------------------------
// Investments
// ---------------------------------------------------------------------------

/**
 * @description Investment-type mix, amounts reconciled to the scope's actual investments total.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 */
export function investmentMix(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
) {
  const investments = core(data, fy, branchId).investments
  const mix = data.constants.investmentMix
  const amounts = largestRemainderRoundDecimal(
    mix.map((i) => investments * i.share),
    investments,
  )
  return mix.map((i, idx) => ({
    key: i.key,
    label: i.label,
    amountCr: amounts[idx]!,
    sharePct: i.share * 100,
  }))
}

// ---------------------------------------------------------------------------
// Asset Quality (Sector-wise NPA)
// ---------------------------------------------------------------------------

/**
 * @description Sector-wise NPA breakdown, scaled by the scope's own NPA% relative to the bank-wide
 * FY25 figure, amounts reconciled to the scope's total NPA amount.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 */
export function npaSectors(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
) {
  const c = core(data, fy, branchId)
  const advances = c.advances
  const yearScale = c.npaPct / data.core.all.FY25.npaPct
  const mix = data.constants.npaSectorMix
  const accounts = npaSectorAccountsForScope(data, fy, branchId, mix)
  const rawAmounts = mix.map((s) => advances * s.pctOfAdvances * yearScale * 100)
  const amounts = largestRemainderRoundDecimal(
    rawAmounts,
    totalNpaAmountLacs(data, fy, branchId),
    2,
  )
  return mix
    .map((s, i) => {
      const pct = s.pctOfAdvances * yearScale
      return {
        key: s.key,
        label: s.label,
        npaPct: pct * 100,
        amountLacs: amounts[i]!,
        accounts: accounts[i]!,
      }
    })
    .sort((a, b) => b.npaPct - a.npaPct)
}

/**
 * @description Splits each sector's bank-wide NPA account count across every real branch by
 * customer share, largest-remainder rounded so the branches always sum back to EXACTLY that
 * sector's bank-wide count. An earlier, per-branch-independent `Math.max(1, Math.round(...))`
 * floored every sector to at least 1 account even for a branch with a negligible true share —
 * harmless-looking on any single branch's page, but summing the "Total NPA Accounts" KPI across
 * all branches came to a real, documented overcount against the bank-wide total. "All Branches"
 * returns the ground-truth counts directly.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} branchId - `ALL_BRANCHES_ID` or a real branch id.
 * @param {NpaSectorEntry[]} mix - The sector mix, for its bank-wide account counts.
 * @returns {number[]} Account counts, in the same order as `mix`.
 */
function npaSectorAccountsForScope(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string,
  mix: NpaSectorEntry[],
): number[] {
  if (branchId === ALL_BRANCHES_ID) return mix.map((s) => s.accounts)

  const bankWideCustomers = core(data, fy, ALL_BRANCHES_ID).totalCustomers
  const branchFractions = BRANCHES.map(
    (branch) => branchCoreYear(data, fy, branch.id).totalCustomers / bankWideCustomers,
  )
  const perSectorPerBranch = mix.map((s) =>
    largestRemainderRound(
      branchFractions.map((fraction) => s.accounts * fraction),
      s.accounts,
    ),
  )

  const branchIdx = BRANCHES.findIndex((branch) => branch.id === branchId)
  if (branchIdx === -1) return mix.map((s) => s.accounts)
  return perSectorPerBranch.map((row) => row[branchIdx]!)
}

/**
 * @description Total NPA amount, in ₹ Lacs.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 * @returns {number} `advances * (npaPct / 100) * 100`.
 */
export function totalNpaAmountLacs(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
): number {
  const c = core(data, fy, branchId)
  return c.advances * (c.npaPct / 100) * 100
}

// ---------------------------------------------------------------------------
// Branch Analytics
// ---------------------------------------------------------------------------

/**
 * @description All-branch income ranking (or scoped to `branchIds`).
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string[]} [branchIds] - Restricts the ranking to these branch ids. Defaults to every
 * branch.
 */
export function branchIncome(data: BankDataPayload, fy: FiscalYear, branchIds?: string[]) {
  const scope = branchIds ? BRANCHES.filter((branch) => branchIds.includes(branch.id)) : BRANCHES
  const rows = scope.map((branch) => ({
    branch,
    incomeLacs: core(data, fy, branch.id).totalIncome * 100,
  }))
  const total = rows.reduce((sum, row) => sum + row.incomeLacs, 0)
  return rows
    .map((row) => ({ ...row, sharePct: (row.incomeLacs / total) * 100 }))
    .sort((a, b) => b.incomeLacs - a.incomeLacs)
}

/**
 * @description Same shape as `branchIncome`, but each branch's figure is summed across the full
 * 5-year window instead of one year — income is additive, so summing (unlike a ratio) is
 * meaningful here.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {string[]} [branchIds] - Restricts the ranking to these branch ids. Defaults to every
 * branch.
 */
export function branchIncome5YSum(data: BankDataPayload, branchIds?: string[]) {
  const scope = branchIds ? BRANCHES.filter((branch) => branchIds.includes(branch.id)) : BRANCHES
  const rows = scope.map((branch) => ({
    branch,
    incomeLacs: sumAcrossYears((fy) => core(data, fy, branch.id).totalIncome) * 100,
  }))
  const total = rows.reduce((sum, row) => sum + row.incomeLacs, 0)
  return rows
    .map((row) => ({ ...row, sharePct: (row.incomeLacs / total) * 100 }))
    .sort((a, b) => b.incomeLacs - a.incomeLacs)
}

/**
 * @description Income/expense composition, spread%, and cost-to-income ratio for a scope.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 */
export function incomeExpenseMix(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
) {
  const c = core(data, fy, branchId)
  const income = c.totalIncome
  const expenses = c.totalExpenses
  const { incomeMixShare, expenseMixShare } = data.constants
  const [interestIncome, nonInterestIncome] = largestRemainderRoundDecimal(
    [income * incomeMixShare.interest, income * incomeMixShare.nonInterest],
    income,
  ) as [number, number]
  const [interestExpense, nonInterestExpense] = largestRemainderRoundDecimal(
    [expenses * expenseMixShare.interest, expenses * expenseMixShare.nonInterest],
    expenses,
  ) as [number, number]
  const netInterestIncome = interestIncome - interestExpense
  const spreadPct = (netInterestIncome / c.advances) * 100
  const ciRatio = (nonInterestExpense / (netInterestIncome + nonInterestIncome)) * 100
  const residual = income - expenses
  return {
    income,
    expenses,
    interestIncome,
    nonInterestIncome,
    interestExpense,
    nonInterestExpense,
    netInterestIncome,
    spreadPct,
    ciRatio,
    residual,
    residualToIncomePct: (residual / income) * 100,
  }
}

// ---------------------------------------------------------------------------
// Productivity & Efficiency
// ---------------------------------------------------------------------------

/**
 * @description Per-employee productivity metrics for a scope. Gross Profit is derived from the
 * bank's real, reported Net Profit plus the assumed per-head Employee Cost — not a second,
 * independent per-head constant — so "Gross Profit − Employee Cost" always ties out exactly to
 * the displayed Net Profit on a waterfall chart.
 * @param {BankDataPayload} data - The full fetched banking dataset.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @param {string} [branchId] - `ALL_BRANCHES_ID` (default) or a real branch id.
 */
export function perEmployeeMetrics(
  data: BankDataPayload,
  fy: FiscalYear,
  branchId: string = ALL_BRANCHES_ID,
) {
  const c = core(data, fy, branchId)
  const employees = Math.max(1, c.employees)
  const business = totalBusiness(data, fy, branchId)
  const netProfit = c.netProfit
  const income = c.totalIncome
  const ownFunds = c.shareCapital + c.reserves
  const workingCapital = c.deposits + c.shareCapital + c.reserves
  const costPerHeadCr = data.constants.employeeCostPerHeadCr
  const totalCostCr = employees * costPerHeadCr
  const totalGrossProfitCr = netProfit + totalCostCr
  const grossProfitPerHeadCr = totalGrossProfitCr / employees
  return {
    employees,
    businessPerEmployeeCr: business / employees,
    costPerEmployeeLacs: costPerHeadCr * 100,
    grossProfitPerEmployeeLacs: grossProfitPerHeadCr * 100,
    netProfitCr: netProfit,
    netProfitPerEmployeeLacs: (netProfit / employees) * 100,
    grossProfitOverOwnFundsPct: (totalGrossProfitCr / ownFunds) * 100,
    costOverWorkingCapitalPct: (totalCostCr / workingCapital) * 100,
    costOverIncomePct: (totalCostCr / income) * 100,
  }
}

// ---------------------------------------------------------------------------
// Display helpers — every page composes its business-summary bullets from numbers already
// produced above, never hand-typed separately.
// ---------------------------------------------------------------------------

/**
 * @description Classifies a YoY/growth value as up/down/flat for a trend arrow.
 * @param {number | null} value - The growth value, or `null` when undefined (e.g. no prior year).
 * @returns {'up' | 'down' | 'flat'} "flat" for `null` or a value within 0.01 of zero.
 */
export function trendLabel(value: number | null): 'up' | 'down' | 'flat' {
  if (value === null || Math.abs(value) < 0.01) return 'flat'
  return value > 0 ? 'up' : 'down'
}

/**
 * @description Re-orders a value-sorted (descending) list by alternating from the top and bottom
 * (largest, smallest, 2nd-largest, 2nd-smallest, ...) so a bar chart reads as an up/down zigzag
 * instead of a monotonic staircase. Ranking-based KPIs (top-N share, rank, etc.) should keep using
 * the original sorted array; only a chart's display order should use this.
 * @template T
 * @param {T[]} sortedDesc - The list, already sorted descending by value.
 * @returns {T[]} The same items, reordered for display.
 */
export function zigzagOrder<T>(sortedDesc: T[]): T[] {
  const out: T[] = []
  let lo = 0
  let hi = sortedDesc.length - 1
  let fromTop = true
  while (lo <= hi) {
    if (fromTop) {
      out.push(sortedDesc[lo]!)
      lo++
    } else {
      out.push(sortedDesc[hi]!)
      hi--
    }
    fromTop = !fromTop
  }
  return out
}
